import { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
// import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
// import { ReactComponent as PlusSquare } from "bootstrap-icons/icons/plus-square.svg";
// import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
// import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../spinner/Spinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../components/utils/CustomStyles";
import formatDate from "../../components/utils/DateFormatter";
import { handleError } from "../../components/utils/AuthenticationCheck";

const AdminActLog = () => {
    const [adminLog, setAdminLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterText, setFilterText] = useState("");
    const [searchQuery, setSearchQuery] = useState(""); // This will hold the actual search term for API
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    // const [confirmationLoading, setConfirmationLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [totalRows, setTotalRows] = useState(0);

    const navigate = useNavigate();

    // Function to fetch admin activity logs
    const fetchAllAdminActList = async (page = 1, pageSize = 50, search = "") => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) {
                throw new Error("No access token found.");
            }

            const response = await axios.get(
                `${ApiServices.getAdminActivityLog}?page=${page}&per_page=${pageSize}&search=${search}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            const { status, message, data } = response.data;
            const activities = data.activities;

            if (status === 200 && Array.isArray(activities)) {
                setAdminLog(activities);
                setCurrentPage(data.page);
                setTotalRows(data.total);
                setPerPage(data.per_page);
                toast.success(message, { toastId: "activity-log-fetch-successfully" });
            } else {
                handleError({ response: { status, data: { message } } }, navigate);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchAllAdminActList(currentPage, perPage, searchQuery);
    }, [navigate]);

    // Refetch data when pagination or search changes
    useEffect(() => {
        fetchAllAdminActList(currentPage, perPage, searchQuery);
    }, [currentPage, perPage, searchQuery]);

    const handleRowChange = (newPerPage) => {
        setPerPage(newPerPage);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    // Handle Page Change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFilter = (e) => {
        const currentFilterText = e.target.value;
        setFilterText(currentFilterText);
        // Don't trigger API call here anymore
    };

    // Handle search button click
    const handleSearch = () => {
        setSearchQuery(filterText);
        setCurrentPage(1); // Reset to first page when searching
        setResetPaginationToggle(!resetPaginationToggle);
    };

    // Handle Enter key press in search input
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear search
    const handleClearSearch = () => {
        setFilterText("");
        setSearchQuery("");
        setCurrentPage(1);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    const columns = [
        {
            name: "Date",
            selector: (row) => (row.created_at ? row.created_at : "NOT AVAILABLE"),
            sortable: true,
            width: "190px",
        },
        {
            name: "Email",
            selector: (row) => (row.email ? row.email : "NOT AVAILABLE"),
            sortable: true,
            width: "200px",
        },
        {
            name: "Role",
            selector: (row) => (row.role ? row.role : "NOT AVAILABLE"),
            sortable: true,
            width: "200px",
        },
        {
            name: "Action",
            selector: (row) => (row.action ? row.action : "NOT AVAILABLE"),
            sortable: true,
            width: "400px",
        },
        {
            name: "Status",
            selector: (row) => (row.status ? row.status : "NOT AVAILABLE"),
            sortable: true,
            width: "150px",
        },
    ];

    // Process data for display
    const data = adminLog.map((log) => ({
        created_at: formatDate(log.created_at),
        action: log.action ? log.action : "NOT AVAILABLE",
        status: log.status ? log.status : "NOT AVAILABLE",
        user_id: log.user_id,
        email: log.email,
        role: log.role,
    }));

    return (
        <div>
            <MainLayouts>
                <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                    <section
                        className="w-full pr-[3%] pt-3 my-3"
                        style={{ backgroundColor: "#F7F7F7" }}
                    >
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Admin Activity
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600">List</p>
                                </li>
                            </ol>
                        </nav>
                    </section>
                    <div className="pb-4 bg-white dark:bg-gray-900">
                        <div className="w-full mb-4">
                            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                                <input
                                    type="search"
                                    className="flex-1 px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white min-w-0"
                                    placeholder="Search by email, role, action, status, or date..."
                                    value={filterText}
                                    onChange={handleFilter}
                                    onKeyPress={handleKeyPress}
                                />
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        onClick={handleSearch}
                                        className="px-6 py-2 bg-darkGreen3 text-lGrey rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-200 whitespace-nowrap"
                                        disabled={loading}
                                    >
                                        {loading ? 'Searching...' : 'Search'}
                                    </button>
                                    {(filterText || searchQuery) && (
                                        <button
                                            onClick={handleClearSearch}
                                            className="px-6 py-2 bg-orange1 text-lGrey rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors duration-200 whitespace-nowrap"
                                            disabled={loading}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <Spinner loading={loading} />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={data}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            paginationPerPage={perPage}
                            paginationRowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
                            onChangeRowsPerPage={handleRowChange}
                            onChangePage={handlePageChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                            striped
                            responsive
                            highlightOnHover
                            noDataComponent={<div>No admin activity log found</div>}
                            customStyles={CustomStyles}
                        />
                    )}
                </div>
            </MainLayouts>
        </div>
    );
};

export default AdminActLog;